/**
 * FELVORA — Vertical LFPIORPI: Operaciones (F3 C1).
 * Alta con partes, evaluación de umbrales + acumulación 6m, gates visibles, semáforo,
 * candado de efectivo Art. 32, captura de BC 25%, botón abstención.
 *
 * feb-2026 · iter 08-02 — Textos visibles neutralizados. El nombre del giro se resuelve
 * dinámicamente vía `giroNombreCorto(user.giro)`. Fase PLD PURA: solo se exponen las
 * operaciones Fr. V (compraventa/intermediación) en el selector. Los tipos "desarrollo"
 * (Fr. V Bis) y "arrendamiento" (Fr. XV) siguen soportados por el motor (backend acepta
 * TIPOS_OPERACION completo) — cuando se prendan en UI, basta con reincluirlos en `TIPOS`.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileText, Plus, ShieldAlert, ShieldCheck, AlertTriangle, DollarSign, Ban, Users, XCircle, Lock, Send, FolderLock } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { giroNombreCorto, giroArticulo } from "@/lib/actividad_vulnerable";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { useMiActividad } from "@/hooks/useMiActividad";

// feb-2026 · iter 08-02 — Solo Fr. V en la UI (fase PLD PURA). Los tipos "desarrollo" y
// "arrendamiento" quedan comentados: el motor los sigue aceptando por API; cuando se
// retomen las fracciones V Bis y XV, se reactivan aquí.
const TIPOS = [
  { v: "compraventa", label: "Compraventa (Fr. V)" },
  { v: "intermediacion", label: "Intermediación (Fr. V)" },
  // { v: "desarrollo", label: "Desarrollo (Fr. V Bis)" },
  // { v: "arrendamiento", label: "Arrendamiento (Fr. XV)" },
];
// ago-2026 · Bloque A — las demás actividades vulnerables NO tienen "compraventa" ni
// "intermediación": sus tipos de acto los declara el catálogo (`tipos_acto` por fracción).
// Inmobiliaria conserva EXACTAMENTE la lista de arriba (fase PLD PURA, cero regresión).
function tiposDeActo(act) {
  if (act.giro === "inmobiliaria" || !act.tiposActo?.length) return TIPOS;
  const bonito = (t) => t.replace(/_/g, " ").replace(/^./, (c) => c.toUpperCase());
  const fr = act.fracciones[0] ? ` (Fr. ${act.fracciones[0]})` : "";
  return act.tiposActo.map((t) => ({ v: t, label: `${bonito(t)}${fr}` }));
}

const FORMAS = ["efectivo", "transferencia", "cheque", "mixto"];
const ROLES = ["comprador", "vendedor", "arrendador", "arrendatario", "aportante"];

const mxn = (n) => new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(n || 0);

const EMPTY = {
  tipo: "compraventa", fecha: new Date().toISOString().slice(0, 10),
  inmueble_id: "", monto_sin_iva: 0, iva: 0,
  renta_mensual_sin_iva: 0, renta_mensual_iva: 0, meses_cubiertos: 1,
  forma_pago: "transferencia", desglose_pago: [],
  partes: [], origen_prospecto_id: "",
};

function SemaforoUmbral({ op }) {
  const u = op?.evaluacion_umbral?.umbral || {};
  const acum = op?.acumulacion_6m || {};
  if (op.estatus === "abstenida") return <Badge className="bg-red-100 text-red-800"><Ban className="mr-1 h-3 w-3 inline" />Abstención Art. 18</Badge>;
  if (op.objeto_de_aviso) return <Badge className="bg-orange-100 text-orange-800"><ShieldAlert className="mr-1 h-3 w-3 inline" />Objeto de aviso</Badge>;
  if (u.alerta_80pct || acum.alerta_80pct) return <Badge className="bg-amber-100 text-amber-800"><AlertTriangle className="mr-1 h-3 w-3 inline" />Alerta 80%</Badge>;
  if (u.identificacion) return <Badge className="bg-blue-100 text-blue-800"><ShieldCheck className="mr-1 h-3 w-3 inline" />Solo identificación</Badge>;
  return <Badge className="bg-slate-100 text-slate-700">Sin obligación</Badge>;
}

export default function Operaciones() {
  const { user } = useAuth();
  const act = useMiActividad();
  const giroCorto = act.nombreCorto || giroNombreCorto(user?.giro);
  // Bloque A — la cita legal sale del catálogo (una actividad puede operar varias fracciones).
  const articulo = act.fracciones.length ? act.articulo : giroArticulo(user?.giro);
  const tiposDisponibles = tiposDeActo(act);
  const [items, setItems] = useState([]);
  const [inmuebles, setInmuebles] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [detalle, setDetalle] = useState(null);
  const [fraccionFiltro, setFraccionFiltro] = useState("");

  const load = () => {
    // feb-2026 · iter 08-02 — Fase PLD PURA: la UI del vertical solo muestra Fr. V.
    // El motor sigue aceptando y almacenando ops de otras fracciones por API; simplemente
    // no las exponemos aquí hasta que se reactiven esas fracciones. Basta con quitar el
    // filtro fijo `fraccion: "V"` cuando se descomente V_bis/XV arriba.
    // ago-2026 · Bloque A — la fracción por defecto la da el catálogo de MI actividad
    // (inmobiliaria sigue entrando con "V": es la primera de su lista).
    const params = { fraccion: fraccionFiltro || act.fraccionesRaw[0] || "V" };
    api.get("/inmobiliaria/operaciones", { params }).then((r) => setItems(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    api.get("/inmobiliaria/inmuebles").then((r) => setInmuebles(r.data)).catch(() => {});
    api.get("/clientes?limit=500").then((r) => setClientes(r.data || [])).catch(() => {});
  }, [fraccionFiltro, act.giro]);

  const addParte = () => setForm({ ...form, partes: [...form.partes, { rol: "comprador", cliente_id: "" }] });
  const rmParte = (idx) => setForm({ ...form, partes: form.partes.filter((_, i) => i !== idx) });
  const editParte = (idx, k, v) => {
    const p = [...form.partes]; p[idx] = { ...p[idx], [k]: v }; setForm({ ...form, partes: p });
  };

  const submit = async () => {
    try {
      const body = { ...form };
      if (form.tipo !== "arrendamiento") { body.renta_mensual_sin_iva = 0; body.renta_mensual_iva = 0; }
      const r = await api.post("/inmobiliaria/operaciones", body);
      toast.success("Operación registrada — revisa el semáforo y los gates");
      setOpen(false); setForm(EMPTY); load(); setDetalle(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : (detail?.mensaje || "Error al registrar"));
    }
  };

  const abrirDetalle = async (id) => {
    try { const r = await api.get(`/inmobiliaria/operaciones/${id}`); setDetalle(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const cerrar = async (id) => {
    try {
      const r = await api.post(`/inmobiliaria/operaciones/${id}/cerrar`);
      toast.success("Operación cerrada");
      abrirDetalle(id); load();
      return r;
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Faltan gates para cerrar");
    }
  };

  const abstencion = async (id) => {
    const motivo = prompt("Motivo de abstención (≥10 caracteres):");
    if (!motivo || motivo.trim().length < 10) { toast.error("Motivo insuficiente"); return; }
    try {
      await api.post(`/inmobiliaria/operaciones/${id}/abstencion`, { motivo });
      toast.success("Abstención Art. 18 registrada");
      abrirDetalle(id); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="operaciones-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-semibold text-slate-900">
            <FileText className="mr-2 inline h-6 w-6 text-emerald-700" /> Operaciones · {giroCorto}
          </h1>
          <p className="text-sm text-slate-500">
            {tiposDisponibles.map((t) => t.label.replace(/\s*\(Fr\..*\)$/, "")).join(" · ")} ({articulo}).
            Umbrales UMA + Art. 32 hardwired.
          </p>
        </div>
        <div className="flex gap-2">
          {/* feb-2026 · iter 08-02 — Filtro de fracción oculto: fase PLD PURA muestra
              exclusivamente Fr. V. Cuando se reactiven V_bis/XV, restaurar el Select. */}
          <Button data-testid="btn-nueva-operacion" onClick={() => { setForm({ ...EMPTY, tipo: tiposDisponibles[0]?.v || EMPTY.tipo }); setOpen(true); }}>
            <Plus className="mr-2 h-4 w-4" /> Nueva operación
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">{items.length} operación(es)</CardTitle></CardHeader>
        <CardContent>
          <div className="divide-y divide-slate-200">
            {items.map((it) => (
              <div key={it.id} className="py-3 cursor-pointer hover:bg-slate-50" data-testid={`op-row-${it.id}`}
                    onClick={() => abrirDetalle(it.id)}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-semibold text-slate-900">
                      {it.inmueble_direccion_snap} · <span className="capitalize">{it.tipo}</span>
                    </p>
                    <p className="text-xs text-slate-500">
                      {it.fecha} · {it.partes.length} parte(s) · {it.forma_pago}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline">Fr. {it.fraccion.replace("_", " ")}</Badge>
                    <SemaforoUmbral op={it} />
                    <Badge variant="secondary">{mxn(it.monto_total || (it.renta_mensual_con_iva * (it.meses_cubiertos || 1)))}</Badge>
                    <Badge className="capitalize">{it.estatus}</Badge>
                  </div>
                </div>
              </div>
            ))}
            {items.length === 0 && <p className="py-6 text-center text-sm text-slate-500">Sin operaciones aún.</p>}
          </div>
        </CardContent>
      </Card>

      {/* Modal alta */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nueva operación</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div><label className="text-sm">Tipo *</label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger data-testid="op-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>{tiposDisponibles.map((t) => <SelectItem key={t.v} value={t.v}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><label className="text-sm">Fecha *</label>
              <Input type="date" data-testid="op-fecha" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} />
            </div>
            <div className="md:col-span-2"><label className="text-sm">{act.bien} *</label>
              <Select value={form.inmueble_id} onValueChange={(v) => setForm({ ...form, inmueble_id: v })}>
                <SelectTrigger data-testid="op-inmueble"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>
                  {inmuebles.map((i) => <SelectItem key={i.id} value={i.id}>{i.direccion} — {i.tipo}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {form.tipo === "arrendamiento" ? (
              <>
                <div><label className="text-sm">Renta mensual sin IVA *</label>
                  <Input type="number" data-testid="op-renta-mensual" value={form.renta_mensual_sin_iva}
                        onChange={(e) => setForm({ ...form, renta_mensual_sin_iva: parseFloat(e.target.value) || 0 })} />
                </div>
                <div><label className="text-sm">IVA de la renta</label>
                  <Input type="number" value={form.renta_mensual_iva}
                        onChange={(e) => setForm({ ...form, renta_mensual_iva: parseFloat(e.target.value) || 0 })} />
                </div>
                <div><label className="text-sm">Meses cubiertos por este pago</label>
                  <Input type="number" data-testid="op-meses" value={form.meses_cubiertos}
                        onChange={(e) => setForm({ ...form, meses_cubiertos: parseInt(e.target.value) || 1 })} />
                </div>
              </>
            ) : (
              <>
                <div><label className="text-sm">Monto sin IVA *</label>
                  <Input type="number" data-testid="op-monto-sin-iva" value={form.monto_sin_iva}
                        onChange={(e) => setForm({ ...form, monto_sin_iva: parseFloat(e.target.value) || 0 })} />
                </div>
                <div><label className="text-sm">IVA</label>
                  <Input type="number" data-testid="op-iva" value={form.iva}
                        onChange={(e) => setForm({ ...form, iva: parseFloat(e.target.value) || 0 })} />
                </div>
              </>
            )}

            <div><label className="text-sm">Forma de pago</label>
              <Select value={form.forma_pago} onValueChange={(v) => setForm({ ...form, forma_pago: v })}>
                <SelectTrigger data-testid="op-forma-pago"><SelectValue /></SelectTrigger>
                <SelectContent>{FORMAS.map((f) => <SelectItem key={f} value={f} className="capitalize">{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>

            <div className="md:col-span-2">
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-semibold">Partes</label>
                <Button size="sm" variant="outline" data-testid="btn-add-parte" onClick={addParte}>
                  <Plus className="h-3 w-3 mr-1" /> Agregar parte
                </Button>
              </div>
              {form.partes.map((p, idx) => (
                <div key={idx} className="mb-2 flex gap-2 items-center" data-testid={`parte-${idx}`}>
                  <Select value={p.rol} onValueChange={(v) => editParte(idx, "rol", v)}>
                    <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
                    <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={p.cliente_id} onValueChange={(v) => editParte(idx, "cliente_id", v)}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Cliente…" /></SelectTrigger>
                    <SelectContent>
                      {clientes.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre || c.razon_social} — {c.rfc || "(sin RFC)"} · {c.tipo_persona}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Button size="icon" variant="ghost" onClick={() => rmParte(idx)}><XCircle className="h-4 w-4" /></Button>
                </div>
              ))}
              {form.partes.length === 0 && <p className="text-xs text-slate-500">Agrega al menos una parte.</p>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="btn-guardar-op" onClick={submit}>Registrar y evaluar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal detalle */}
      <Dialog open={!!detalle} onOpenChange={(v) => !v && setDetalle(null)}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Detalle de la operación</DialogTitle></DialogHeader>
          {detalle && <DetalleOperacion op={detalle} onCerrar={() => cerrar(detalle.id)} onAbstencion={() => abstencion(detalle.id)} clientes={clientes} onReload={() => abrirDetalle(detalle.id)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DetalleOperacion({ op, onCerrar, onAbstencion, clientes, onReload }) {
  const act = useMiActividad();
  const gates = op.gates || {};
  const [bcOpen, setBcOpen] = useState(false);
  const [bcParteIdx, setBcParteIdx] = useState(null);
  const [bcCid, setBcCid] = useState(""); const [bcPct, setBcPct] = useState(25);
  const [expediente, setExpediente] = useState(null);
  const [expedienteOpen, setExpedienteOpen] = useState(false);
  const acum = op.acumulacion_6m || {};

  const agregarBc = async () => {
    if (!bcCid || bcPct < 25) { toast.error("Cliente y porcentaje ≥25% obligatorios"); return; }
    try {
      await api.post(`/inmobiliaria/operaciones/${op.id}/partes/${bcParteIdx}/bc`, { bc_cliente_id: bcCid, porcentaje: bcPct });
      toast.success("BC agregado");
      setBcOpen(false); setBcCid(""); setBcPct(25); onReload();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const generar24h = async () => {
    const motivo = prompt("Motivo del aviso 24h (LPB / indicio / URE):");
    if (!motivo || motivo.trim().length < 5) { toast.error("Motivo insuficiente"); return; }
    try {
      const body = { operacion_id: op.id, motivo, origen_lpb: !gates.screening?.ok };
      const r = await api.post("/inmobiliaria/avisos/24h", body);
      toast.success(`Aviso 24h creado (${r.data.estatus}) — reloj corriendo`);
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const verExpediente = async () => {
    try {
      const r = await api.get(`/inmobiliaria/conservacion/expediente/${op.id}`);
      setExpediente(r.data); setExpedienteOpen(true);
    } catch (e) { toast.error("Error cargando expediente"); }
  };

  return (
    <div className="space-y-3 text-sm" data-testid="op-detalle">
      <div className="flex flex-wrap gap-2">
        <Badge variant="outline">Fr. {op.fraccion.replace("_", " ")}</Badge>
        <SemaforoUmbral op={op} />
        <Badge>{op.fecha}</Badge>
        <Badge variant="secondary" className="capitalize">{op.estatus}</Badge>
        {op.asesor_user_id && <Badge variant="outline">Asesor asignado</Badge>}
      </div>

      <div>
        <p className="font-semibold">{act.bien}: {op.inmueble_direccion_snap}</p>
        <p className="text-xs text-slate-500">tipo: {op.tipo} · forma_pago: {op.forma_pago}</p>
      </div>

      <div className="rounded border border-slate-200 p-3">
        <p className="font-semibold mb-1">Evaluación de umbral</p>
        <p className="text-xs">UMA vigente: <span className="font-mono">{op.evaluacion_umbral?.uma_diario}</span> ({op.evaluacion_umbral?.uma_vigencia?.inicio} → {op.evaluacion_umbral?.uma_vigencia?.fin})</p>
        {op.fraccion === "XV" ? (
          <div className="mt-1 text-xs">
            <p>Renta mensual evaluada: <strong>{mxn(op.evaluacion_umbral?.umbral?.renta_mensual_evaluada_mxn)}</strong></p>
            <p>Umbral identificación: {mxn(op.evaluacion_umbral?.umbral?.umbral_identificacion_mxn)}</p>
            <p>Umbral aviso: {mxn(op.evaluacion_umbral?.umbral?.umbral_aviso_mxn)}</p>
          </div>
        ) : (
          <div className="mt-1 text-xs">
            <p>Monto sin IVA: <strong>{mxn(op.monto_sin_iva)}</strong></p>
            <p>Umbral aviso: {mxn(op.evaluacion_umbral?.umbral?.umbral_aviso_mxn)}</p>
            <p>Porcentaje alcanzado: {op.evaluacion_umbral?.umbral?.porcentaje_alcanzado}%</p>
          </div>
        )}
      </div>

      {acum.aplica && (
        <div className="rounded border border-amber-200 bg-amber-50 p-3">
          <p className="font-semibold mb-1">Acumulación 6 meses</p>
          <p className="text-xs">Acumulado previo: {mxn(acum.acumulado_previo_6m_mxn)} · Este evento: {mxn(acum.monto_este_evento_mxn)}</p>
          <p className="text-xs">Total 6m: <strong>{mxn(acum.acumulado_total_6m_mxn)}</strong> · Umbral: {mxn(acum.umbral_aviso_mxn)} ({acum.porcentaje_alcanzado}%)</p>
          {acum.objeto_de_aviso_por_acumulacion && (
            <Badge className="bg-orange-100 text-orange-800 mt-1">Objeto de aviso por acumulación · periodo reiniciado</Badge>
          )}
        </div>
      )}

      <div className="rounded border border-slate-200 p-3">
        <p className="font-semibold mb-1">Gates para cerrar</p>
        <ul className="text-xs space-y-1">
          <li className={gates.screening?.ok ? "text-emerald-700" : "text-red-700"}>
            {gates.screening?.ok ? "✓" : "✗"} Screening: {gates.screening?.motivo || "OK"}
          </li>
          <li className={gates.expediente?.ok ? "text-emerald-700" : "text-red-700"}>
            {gates.expediente?.ok ? "✓" : "✗"} Expediente {gates.expediente?.exige ? "(requerido)" : "(no requerido)"} · faltantes: {(gates.expediente?.partes_incompletas || []).join(", ") || "ninguno"}
          </li>
          <li className={gates.beneficiario_controlador?.ok ? "text-emerald-700" : "text-red-700"}>
            {gates.beneficiario_controlador?.ok ? "✓" : "✗"} Beneficiario controlador ≥ 25% · PM sin BC: {(gates.beneficiario_controlador?.partes_sin_bc || []).join(", ") || "ninguna"}
          </li>
          <li className={gates.efectivo?.ok ? "text-emerald-700" : "text-red-700"}>
            {gates.efectivo?.ok ? "✓" : "✗"} Candado efectivo (Art. {gates.efectivo?.articulo || "32"}) · límite {mxn(gates.efectivo?.limite_mxn)}
          </li>
        </ul>
      </div>

      <div>
        <p className="font-semibold">Partes ({op.partes.length})</p>
        {op.partes.map((p, i) => (
          <div key={i} className="mt-1 rounded border border-slate-200 p-2 text-xs" data-testid={`parte-det-${i}`}>
            <p><strong className="capitalize">{p.rol}:</strong> {p.cliente_snapshot_nombre} · {p.tipo_persona}</p>
            <p>Expediente: {p.expediente_completo ? "Completo" : "Incompleto"}</p>
            {(p.tipo_persona === "Persona moral" || p.tipo_persona === "Fideicomiso") && (
              <div className="mt-1">
                <div className="flex items-center justify-between">
                  <span>BCs ({(p.bcs || []).length}):</span>
                  {op.estatus !== "cerrada" && op.estatus !== "abstenida" && (
                    <Button size="sm" variant="outline" data-testid={`btn-add-bc-${i}`}
                            onClick={() => { setBcParteIdx(i); setBcOpen(true); }}>
                      <Users className="h-3 w-3 mr-1" /> Agregar BC
                    </Button>
                  )}
                </div>
                <ul className="ml-4 list-disc">
                  {(p.bcs || []).map((b) => (
                    <li key={b.id}>{b.nombre_snap} — {b.porcentaje}%</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ))}
      </div>

      {(op.aportaciones || []).length > 0 && (
        <div>
          <p className="font-semibold">Aportaciones{act.fracciones.includes("V Bis") ? " (Fr. V Bis)" : ""}</p>
          <ul className="ml-4 list-disc text-xs">
            {op.aportaciones.map((a) => (
              <li key={a.id}>
                {a.fecha} · {mxn(a.monto_total)} · {a.forma_pago} · {a.objeto_de_aviso ? "Objeto de aviso" : (a.objeto_de_aviso_por_acumulacion ? "Aviso por acumulación" : "OK")}
              </li>
            ))}
          </ul>
        </div>
      )}

      {op.estatus !== "cerrada" && op.estatus !== "abstenida" && (
        <div className="flex flex-wrap gap-2 pt-2">
          <Button data-testid="btn-cerrar-op" onClick={onCerrar}><Lock className="mr-1 h-4 w-4" /> Cerrar operación</Button>
          <Button variant="outline" data-testid="btn-abstencion" onClick={onAbstencion}>
            <Ban className="mr-1 h-4 w-4" /> Abstención Art. 18
          </Button>
          {!gates.screening?.ok && (
            <Button variant="destructive" data-testid="btn-generar-24h" onClick={generar24h}>
              <Send className="mr-1 h-4 w-4" /> Generar aviso 24h (LPB)
            </Button>
          )}
          {gates.screening?.ok && op.objeto_de_aviso && (
            <Button variant="outline" data-testid="btn-generar-24h-manual" onClick={generar24h}>
              <Send className="mr-1 h-4 w-4" /> Generar aviso 24h
            </Button>
          )}
        </div>
      )}

      <div className="pt-2 border-t border-slate-100">
        <Button size="sm" variant="ghost" onClick={verExpediente} data-testid="btn-ver-expediente">
          <FolderLock className="mr-1 h-4 w-4" /> Ver expediente de conservación (10 años)
        </Button>
      </div>

      {/* Modal BC */}
      <Dialog open={bcOpen} onOpenChange={setBcOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Agregar Beneficiario Controlador (Art. 33 Bis · ≥25%)</DialogTitle></DialogHeader>
          <div className="space-y-2">
            <label className="text-sm">Beneficiario (Persona Física, cliente existente):</label>
            <Select value={bcCid} onValueChange={setBcCid}>
              <SelectTrigger data-testid="select-bc-cliente"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
              <SelectContent>
                {clientes.filter((c) => c.tipo_persona !== "Persona moral").map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nombre} — {c.rfc || "(sin RFC)"}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="text-sm">Porcentaje de control (≥ 25):</label>
            <Input type="number" data-testid="input-bc-pct" value={bcPct} onChange={(e) => setBcPct(parseFloat(e.target.value) || 0)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBcOpen(false)}>Cancelar</Button>
            <Button data-testid="btn-guardar-bc" onClick={agregarBc}>Guardar BC</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal expediente de conservación */}
      <Dialog open={expedienteOpen} onOpenChange={setExpedienteOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Expediente de conservación · 10 años (LFPIORPI Art. 22)</DialogTitle></DialogHeader>
          {expediente && (
            <div className="space-y-3 text-sm" data-testid="expediente-panel">
              <p className="text-xs text-slate-500">
                Todos los artefactos regulatorios de esta operación (XML, acuses, KYC) con hash SHA-256 y candado hasta 10 años calendario.
              </p>
              <div>
                <p className="font-semibold">Artefactos SPPLD ({expediente.artefactos_conservacion?.length || 0})</p>
                <ul className="ml-4 list-disc text-xs">
                  {(expediente.artefactos_conservacion || []).map((a) => (
                    <li key={a.id} data-testid={`art-${a.id}`}>
                      <span className="capitalize">{a.tipo}</span> · {a.nombre_archivo} ·
                      candado hasta <span className="font-mono">{(a.fecha_conservar_hasta || "").slice(0, 10)}</span>
                      · SHA-256 <span className="font-mono">{(a.hash_sha256 || "").slice(0, 12)}…</span>
                    </li>
                  ))}
                  {(expediente.artefactos_conservacion || []).length === 0 && (
                    <li className="italic text-slate-400">Sin artefactos aún.</li>
                  )}
                </ul>
              </div>
              <div>
                <p className="font-semibold">Avisos SPPLD ({expediente.avisos_sppld?.length || 0})</p>
                <ul className="ml-4 list-disc text-xs">
                  {(expediente.avisos_sppld || []).map((a) => (
                    <li key={a.id}>
                      <span className="capitalize">{a.tipo}</span> · {a.fraccion} · {a.periodo} · <em>{a.estatus}</em>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-semibold">Documentos KYC de partes ({expediente.documentos_kyc_partes?.length || 0})</p>
                <ul className="ml-4 list-disc text-xs">
                  {(expediente.documentos_kyc_partes || []).map((d) => (
                    <li key={d.id}>{d.tipo_documento} · <em>{d.estatus}</em></li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
